import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { getImageBlobName } from "@ankaadia/ankaadia-shared";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { TranslocoService } from "@jsverse/transloco";
import { ConfirmationService as OldConfirmationService } from "primeng-v17/api";
import { ConfirmationService, MenuItem, MenuItemCommandEvent, PrimeIcons } from "primeng/api";
import { Avatar } from "primeng/avatar";
import { Menu } from "primeng/menu";
import { environment } from "../../../../environments/environment";
import { MessageDialogService } from "../../../shared/message-dialog/message-dialog.service";
import { loadPixoEditorSDK, pixoEditorApiKey } from "../../../shared/pixoeditor/pixoeditor-sdk";
import { FileUploadService } from "../../../shared/services/file-upload.service";
import { SettingsService } from "../../../shared/services/settings.service";
import { CandidatesService } from "../../candidates/candidates.service";
import { DocumentsService } from "../../documents/documents.service";

@Component({
  selector: "new-ui-candidate-profile-image",
  templateUrl: "./new-ui-candidate-profile-image.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FileUploadService],
  imports: [Avatar, Menu],
})
export class NewUiCandidateProfileImageComponent implements OnInit {
  url: string;
  noFile = false;
  items: MenuItem[] | undefined;

  @ViewChild("file") file: ElementRef;
  @ViewChild(Avatar) private readonly avatarRef: Avatar;

  private readonly settings = inject(SettingsService);
  private readonly candidateService = inject(CandidatesService);
  private readonly documentService = inject(DocumentsService);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly errorService = inject(MessageDialogService);
  private readonly transloco = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    @Inject(environment.newUI ? ConfirmationService : OldConfirmationService)
    private readonly confirmationService: OldConfirmationService | ConfirmationService
  ) {}

  ngOnInit(): void {
    if (this.settings.isCandidate) {
      this.candidateService
        .getPictureDownloadURL(this.settings.organizationId, this.settings.userOrCandidateId, "picture")
        .subscribe((x) => {
          this.url = x;
          this.updateMenu(this.url == null);
          this.cdr.markForCheck();
        });
    } else {
      this.updateMenu(false);
    }

    this.fileUploadService.onUpload.subscribe(() => {
      this.reloadImage();
      this.updateMenu(false);
      this.cdr.markForCheck();
      this.documentService
        .notifyProfilePictureUpdated(this.settings.organizationId, this.settings.userOrCandidateId)
        .subscribe(() => {
          this.updateMenu(false);
          this.cdr.markForCheck();
        });
    });
    this.fileUploadService.onError.subscribe((e) => {
      this.errorService.showMessage(translateSafely("file.uploadFailed"), (<Error>(e as unknown)).message ?? e.body);
    });
  }

  upload(file: File): void {
    if (file) {
      this.documentService
        .getUploadUrl(
          "DefaultContext",
          getImageBlobName(this.settings.userOrCandidateId, "picture"),
          this.settings.organizationId,
          this.settings.userOrCandidateId,
          true
        )
        .subscribe(({ url }) => this.fileUploadService.upload(file, url, null, "image"));
    }
  }

  onEdit(): void {
    loadPixoEditorSDK().subscribe(() => {
      const editor = new (<any>window).Pixo.Bridge({
        apikey: pixoEditorApiKey,
        type: "modal",
        height: "80%",
        width: "80%",
        theme: "Light",
        language: this.transloco.getActiveLang(),
        output: {
          format: "jpeg",
          quality: 0.6,
          optimize: true,
        },
        styles: { logosrc: "none" },
        features: ["crop", "transform", "adjustments"],
        filterCropSizes(): [{ caption: string; value: number }] {
          return [{ caption: translateSafely("pixo.square"), value: 1 }];
        },
        onSave: (arg): void => {
          this.documentService
            .getUploadUrl(
              "DefaultContext",
              getImageBlobName(this.settings.userOrCandidateId, "picture"),
              this.settings.organizationId,
              this.settings.userOrCandidateId,
              true
            )
            .subscribe(({ url }) => this.fileUploadService.upload(arg.toBlob() as Blob, url));
        },
      });
      editor.edit(this.avatarRef.el.nativeElement.querySelector("img"));
    });
  }

  protected onDelete(_event: MenuItemCommandEvent): void {
    const accept = (): void => {
      this.documentService
        .deleteBlob({
          blobName: getImageBlobName(this.settings.userOrCandidateId, "picture"),
          organizationId: this.settings.organizationId,
          candidateId: this.settings.userOrCandidateId,
          fromImageContainer: true,
        })
        .pipe()
        .subscribe(() => {
          this.reloadImage();
          this.cdr.markForCheck();
        });
    };

    this.confirmationService.confirm({
      target: this.avatarRef.el.nativeElement,
      acceptLabel: translateSafely("common.delete"),
      rejectLabel: translateSafely("common.cancel"),
      message: translateSafely("candidate.image.confirmDelete"),
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      accept: () => accept(),
    });
  }

  protected pictureNotLoaded(): void {
    if (this.url != null) {
      this.noFile = true;
      this.updateMenu(true);
      this.cdr.markForCheck();
    }
  }

  private updateMenu(isImageNull: boolean): void {
    this.items = [
      {
        label: translateSafely("options.title"),
        items: [
          {
            label: translateSafely("primeng.upload"),
            icon: PrimeIcons.UPLOAD,
            command: (): void => this.file.nativeElement.click(),
          },
          {
            label: translateSafely("common.edit"),
            icon: PrimeIcons.PENCIL,
            command: (): void => this.onEdit(),
            disabled: isImageNull,
          },
          {
            label: translateSafely("common.delete"),
            icon: PrimeIcons.TRASH,
            command: (event): void => this.onDelete(event),
          },
        ],
      },
    ];
  }

  private reloadImage(): void {
    const temp = this.url;
    setTimeout(() => {
      this.url = null;
      this.cdr.markForCheck();
    }, 0);
    setTimeout(() => {
      this.noFile = false;
      this.url = temp;
      this.cdr.markForCheck();
    }, 30);
  }
}
